// components/asset-management/assetModelStore.ts
// Per-asset model state for the repositioning business plan, held in the Kernel's
// typed Property runtime overlay (PropertyRuntimeState.assetModel), keyed by
// propertyId:
//   - the editable ScenarioParams (assumptions),
//   - agent/user-authored custom sheets (free-form tabs),
//   - lease-level rent-roll edits.
// The agent's ASSET_MODEL_* commands patch here; assetModelContext reads the
// slice back to describe the live model to the agent. Client-only state: the
// runtime overlay is never persisted on the Property DTO.
'use client';

import type { AssetModelCustomSheet, AssetModelRuntimeState, KernelHandle } from '#types/types';
import type { ScenarioParams, RentRollEdit } from './scenario-engine';
import type { CellStyle } from './importSpreadsheet';

const newSheetId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `cs_${crypto.randomUUID().slice(0, 8)}`;
    }
  } catch {
    /* noop */
  }
  return `cs_${Math.random().toString(36).slice(2, 10)}`;
};

/** Convert a row-major table of strings into a sparse "r,c" -> value cell map. */
const rowsToCells = (rows: string[][]): Record<string, string> => {
  const cells: Record<string, string> = {};
  rows.forEach((row, r) => {
    if (!Array.isArray(row)) return;
    row.forEach((v, c) => {
      if (v === undefined || v === null) return;
      const s = String(v);
      if (s !== '') cells[`${r},${c}`] = s;
    });
  });
  return cells;
};

const maxRowOf = (cells: Record<string, string>): number => {
  let max = -1;
  for (const key of Object.keys(cells)) {
    const r = Number(key.split(',')[0]);
    if (Number.isFinite(r) && r > max) max = r;
  }
  return max;
};

/** The property's asset-model slice of the Kernel runtime overlay (empty when never touched). */
const readAssetModelRuntime = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
): AssetModelRuntimeState =>
  (propertyId ? kernelHandle?.readEntityRuntimeState('Property', propertyId).assetModel : undefined) ?? {};

const patchAssetModelRuntime = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
  patch: Partial<AssetModelRuntimeState>,
): void => {
  if (!kernelHandle || !propertyId) return;
  kernelHandle.patchEntityRuntimeState('Property', propertyId, {
    assetModel: { ...readAssetModelRuntime(kernelHandle, propertyId), ...patch },
  });
};

// --- assumptions ---
export const getAssetModelParams = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
): ScenarioParams | undefined => readAssetModelRuntime(kernelHandle, propertyId).params;

export const setAssetModelParams = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
  params: ScenarioParams,
): void => patchAssetModelRuntime(kernelHandle, propertyId, { params });

export const patchAssetModelParams = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
  patch: Partial<ScenarioParams>,
): void => {
  const current = readAssetModelRuntime(kernelHandle, propertyId).params;
  if (!current) return;
  patchAssetModelRuntime(kernelHandle, propertyId, { params: { ...current, ...patch } });
};

// --- custom sheets ---
const getAssetCustomSheets = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
): AssetModelCustomSheet[] => readAssetModelRuntime(kernelHandle, propertyId).customSheets ?? [];

/** Create or update a custom tab by name. mode 'replace' overwrites cells; 'append' adds rows below.
 *  `formats` carries Excel number-format codes and `styles` the visual styles (both keyed "r,c",
 *  same indexing as rows) from import. */
export const upsertAssetCustomSheet = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
  name: string,
  rows: string[][],
  mode: 'replace' | 'append' = 'replace',
  formats?: Record<string, string>,
  styles?: Record<string, CellStyle>,
): void => {
  const cleanName = name.trim();
  if (!cleanName) return;
  const list = getAssetCustomSheets(kernelHandle, propertyId);
  const idx = list.findIndex((sh) => sh.name.toLowerCase() === cleanName.toLowerCase());
  const incoming = rowsToCells(rows);
  const incomingFormats = formats ?? {};
  const incomingStyles = styles ?? {};
  let nextList: AssetModelCustomSheet[];
  if (idx >= 0) {
    const prev = list[idx]!;
    let cells: Record<string, string>;
    let cellFormats: Record<string, string>;
    let cellStyles: Record<string, CellStyle>;
    if (mode === 'append') {
      const offset = maxRowOf(prev.cells) + 1;
      const shifted: Record<string, string> = {};
      const shiftedFmts: Record<string, string> = {};
      const shiftedStyles: Record<string, CellStyle> = {};
      for (const [k, v] of Object.entries(incoming)) {
        const [r, c] = k.split(',');
        shifted[`${Number(r) + offset},${c}`] = v;
      }
      for (const [k, v] of Object.entries(incomingFormats)) {
        const [r, c] = k.split(',');
        shiftedFmts[`${Number(r) + offset},${c}`] = v;
      }
      for (const [k, v] of Object.entries(incomingStyles)) {
        const [r, c] = k.split(',');
        shiftedStyles[`${Number(r) + offset},${c}`] = v;
      }
      cells = { ...prev.cells, ...shifted };
      cellFormats = { ...(prev.cellFormats ?? {}), ...shiftedFmts };
      cellStyles = { ...(prev.cellStyles ?? {}), ...shiftedStyles };
    } else {
      cells = incoming;
      cellFormats = incomingFormats; // replace: cells are wholly new, so formats/styles are too
      cellStyles = incomingStyles;
    }
    nextList = list.map((sh, i) => (i === idx ? { ...sh, cells, cellFormats, cellStyles } : sh));
  } else {
    nextList = [...list, { id: newSheetId(), name: cleanName, cells: incoming, cellFormats: incomingFormats, cellStyles: incomingStyles }];
  }
  patchAssetModelRuntime(kernelHandle, propertyId, { customSheets: nextList });
};

/** Remove a custom tab by name (case-insensitive). */
export const removeAssetCustomSheet = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
  name: string,
): void => {
  const list = getAssetCustomSheets(kernelHandle, propertyId);
  const nextList = list.filter((sh) => sh.name.toLowerCase() !== name.trim().toLowerCase());
  if (nextList.length === list.length) return;
  patchAssetModelRuntime(kernelHandle, propertyId, { customSheets: nextList });
};

/** Merge cells/formulas into a named custom sheet (create if missing). */
export const writeAssetCustomSheetCells = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
  name: string,
  cells: Record<string, string>,
  createIfMissing = true,
): void => {
  const cleanName = name.trim();
  if (!cleanName) return;
  const list = getAssetCustomSheets(kernelHandle, propertyId);
  const idx = list.findIndex((sh) => sh.name.toLowerCase() === cleanName.toLowerCase());
  let nextList: AssetModelCustomSheet[];
  if (idx >= 0) {
    nextList = list.map((sh, i) => (i === idx ? { ...sh, cells: { ...sh.cells, ...cells } } : sh));
  } else {
    if (!createIfMissing) return;
    nextList = [...list, { id: newSheetId(), name: cleanName, cells: { ...cells } }];
  }
  patchAssetModelRuntime(kernelHandle, propertyId, { customSheets: nextList });
};

// --- rent roll ---
export const getAssetRentRollEdits = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
): Record<string, RentRollEdit> => readAssetModelRuntime(kernelHandle, propertyId).rentRollEdits ?? {};

/** Merge lease-level rent-roll edits (agent/user). */
export const applyAssetRentRollEdits = (
  kernelHandle: KernelHandle | null | undefined,
  propertyId: string,
  edits: Record<string, RentRollEdit>,
): void => {
  const next: Record<string, RentRollEdit> = { ...getAssetRentRollEdits(kernelHandle, propertyId) };
  for (const [k, e] of Object.entries(edits)) {
    next[k] = { ...(next[k] ?? {}), ...e };
  }
  patchAssetModelRuntime(kernelHandle, propertyId, { rentRollEdits: next });
};
